import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { UploadsService } from '../uploads.service';
import { shareInflightRequest } from '../../utils/share-inflight-request.util';
import { CapturePositionCoordinates } from '../../interfaces/api/capture-position-coordinates.interface';
import { ICapturesService } from '../../interfaces/services/captures-service.interface';
import { Capture } from '../../interfaces/state/entities/capture.interface';
import { environment } from '../../environments/environment';
import {
  completeBlobUploadAfterPut,
  putBlobWithProgress,
  resolveBlobPutUrl,
} from '../../utils/blob-upload.utils';

@Injectable({
  providedIn: 'root',
})
export class CapturesService implements ICapturesService {
  private http = inject(HttpClient);
  private uploads = inject(UploadsService);
  private apiUrl = environment.MayaTestToolApiBaseUrl;
  private readonly inflightCaptures = new Map<string, Observable<Capture[]>>();

  getCaptures(sessionId: string): Observable<Capture[]> {
    return shareInflightRequest(this.inflightCaptures, sessionId, () => {
      const params = new HttpParams().set('captureSessionId', sessionId);
      return this.http.get<unknown>(`${this.apiUrl}/Captures`, { params }).pipe(
        map((body) => {
          const list = this.extractCapturesArray(body);
          return list.map((raw, index) => this.mapCaptureFromApi(raw, sessionId, index));
        })
      );
    });
  }

  addCapture(
    sessionId: string,
    name: string,
    file: File,
    position: CapturePositionCoordinates
  ): Observable<Capture> {
    const fileType = this.resolveFileType(file.name);
    const capturedAt = new Date(file.lastModified);
    const body = {
      captureSessionId: sessionId,
      capturedAt: capturedAt.toISOString(),
      fileType,
      position,
      captureName: name,
    };

    return this.http
      .post<{ captureId: string; uploadUrl: string }>(`${this.apiUrl}/Captures`, body)
      .pipe(
        switchMap(({ captureId, uploadUrl }) =>
          this.uploadCaptureFile(captureId, file, uploadUrl).pipe(
            map(() => ({
              id: captureId,
              sessionId,
              name,
              fileName: file.name,
              status: 'Pending',
              dateCreated: new Date(),
              captureAt: capturedAt,
              fileSize: String(file.size),
              revisionNumber: 0,
            }))
          )
        )
      );
  }

  retryProcessingCapture(captureId: string, fileSize: number): Observable<void> {
    return this.http
      .post(`${this.apiUrl}/ProcessedCaptures`, { captureId, fileSize })
      .pipe(map(() => undefined));
  }

  private uploadCaptureFile(
    captureId: string,
    file: File,
    uploadUrl: string,
    useProvidedUploadUrl = false
  ): Observable<void> {
    this.uploads.start(captureId, file.name, 'capture', file.size);
    return from(resolveBlobPutUrl(captureId, file.name, uploadUrl, useProvidedUploadUrl)).pipe(
      switchMap((putUrl) =>
        putBlobWithProgress(this.http, putUrl, file, (loaded) => this.uploads.progress(captureId, loaded))
      ),
      map(() => true),
      catchError(() => of(false)),
      switchMap((uploadSucceeded) =>
        completeBlobUploadAfterPut(this.http, this.apiUrl, uploadSucceeded, 'ProcessedCaptures', {
          captureId,
          fileSize: file.size,
        }).pipe(
          tap({
            next: () => this.uploads.finish(captureId, uploadSucceeded),
            error: () => this.uploads.finish(captureId, false),
          })
        )
      )
    );
  }

  private extractCapturesArray(body: unknown): unknown[] {
    if (Array.isArray(body)) {
      return body;
    }
    if (body != null && typeof body === 'object') {
      const o = body as Record<string, unknown>;
      const keys = ['captures', 'Captures'];
      for (const key of keys) {
        const v = o[key];
        if (Array.isArray(v)) {
          return v;
        }
      }
    }
    return [];
  }

  private mapCaptureFromApi(raw: unknown, sessionId: string, index: number): Capture {
    const r = (raw ?? {}) as Record<string, unknown>;
    const captureType = String(r['captureType'] ?? r['CaptureType'] ?? '');
    const processedRaw = r['processedAt'] ?? r['ProcessedAt'];
    const createdRaw = r['createdAt'] ?? r['CreatedAt'];
    const capturedRaw = r['capturedAt'] ?? r['CapturedAt'];
    const fileSizeRaw = r['fileSize'] ?? r['FileSize'];
    const processIdRaw = r['processId'] ?? r['ProcessId'];
    const processId = processIdRaw != null ? String(processIdRaw).trim() : '';

    return {
      id: String(r['captureId'] ?? r['CaptureId'] ?? ''),
      sessionId,
      name: String(r['captureName'] ?? r['CaptureName'] ?? 'Capture'),
      fileName: this.inferFileNameFromType(captureType),
      status: String(r['status'] ?? r['Status'] ?? ''),
      processId: processId || undefined,
      dateCreated: createdRaw ? new Date(String(createdRaw)) : new Date(),
      captureAt: capturedRaw ? new Date(String(capturedRaw)) : undefined,
      dateProcessed: processedRaw ? new Date(String(processedRaw)) : undefined,
      fileSize: fileSizeRaw != null ? String(fileSizeRaw) : undefined,
      revisionNumber: index + 1,
    };
  }

  private inferFileNameFromType(captureType: string): string | undefined {
    if (captureType === 'Photo360') {
      return 'capture.jpg';
    }
    if (captureType === 'Video') {
      return 'capture.mp4';
    }
    return undefined;
  }

  private resolveFileType(fileName: string): string {
    const dot = fileName.lastIndexOf('.');
    const ext = dot > 0 ? fileName.slice(dot + 1).toLowerCase() : '';
    if (ext === 'jpeg') {
      return 'jpeg';
    }
    if (ext === 'jpg' || ext === 'mp4' || ext === 'insv') {
      return ext;
    }
    return ext || 'jpg';
  }
}
